import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { AnalyticsPageName } from 'src/analytics/analytics.models'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeeds/CardFeeds.style'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import Box from 'src/components/Box/Box'
import SvgFilterFunnel from 'src/components/Svgs/SvgFilterFunnel'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { homeUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'

const CardFeeds: React.FC = () => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const history = useHistory()

  const { data: featuresData, isLoading: isFeaturesLoading } =
    useQueryUserFeatures([UserFeature.BULK_CREDIT_CARD_ENROLLMENT])
  const bulkCreditCardEnrollmentEnabled =
    featuresData?.BULK_CREDIT_CARD_ENROLLMENT ?? false
  const { enable_card_feeds: cardFeedsEnabled } =
    useSelector(selectFeatureFlaggerFromState) ?? {}

  const isAllowed = bulkCreditCardEnrollmentEnabled && !!cardFeedsEnabled

  const [searchValue, setSearchValue] = useState('')

  // Defensive: sidebar gates the menu entry, but a direct URL must redirect
  // home when either flag is off. Wait for the feature query to settle so we
  // don't bounce away during the initial paint.
  useEffect(() => {
    if (isFeaturesLoading) return
    if (!isAllowed) {
      history.replace(homeUrl())
    }
  }, [isAllowed, isFeaturesLoading, history])

  useEffect(() => {
    if (!isAllowed) return
    analytics.page(AnalyticsPageName.OrgSettingsBankFeedsTab)
    analytics.track('bank feeds', { action: 'viewed' })
  }, [analytics, isAllowed])

  if (!isAllowed) {
    return null
  }

  // TODO: wire the real help-guide URL when product provides it; until then
  // we only track the click — opening `#` in a new tab would just land the
  // user back on the current page, which is worse than a no-op.
  const handleHelpGuideClick = (): void => {
    analytics.track('bank feeds', { action: 'help guide clicked' })
  }

  const handleAddBankFeedClick = (): void => {
    analytics.track('bank feeds', { action: 'add bank feed clicked' })
  }

  const handleConnectClick = (): void => {
    analytics.track('bank feeds', { action: 'connect bank feed clicked' })
  }

  const actions = (
    <div css={style.actions}>
      <Button
        variant="secondary"
        onClick={handleHelpGuideClick}
        data-testid="bank-feeds-help-guide"
        aria-label={translate('Help Guide (opens in new tab)')}
      >
        {translate('Help Guide')}
      </Button>
      <Button
        variant="primary"
        onClick={handleAddBankFeedClick}
        data-testid="bank-feeds-add"
      >
        {`+ ${translate('Add Bank Feed')}`}
      </Button>
    </div>
  )

  return (
    <>
      <Helmet>
        <title>{translate('Bank Feeds')}</title>
      </Helmet>
      <LayoutPage
        title={<span>{translate('Bank Feeds')}</span>}
        subtitle={
          <span css={style.subtitle}>
            {translate(
              'Connect your bank to unlock real-time transaction data and seamlessly manage employee card spend.',
            )}
          </span>
        }
        actions={actions}
      >
        <Box noPadding outlineBorder css={style.tableCard}>
          <div css={style.toolbar}>
            <div css={style.search}>
              <TextInput
                value={searchValue}
                onChange={(e): void => setSearchValue(e.target.value)}
                placeholder={translate('Search by...')}
                aria-label={translate('Search bank feeds')}
                testId="bank-feeds-search"
              />
            </div>
            <Button
              variant="toolbar"
              dropShadow={false}
              onClick={(): void => undefined}
              data-testid="bank-feeds-filter"
              aria-label={translate('Toggle filters')}
            >
              <SvgFilterFunnel />
            </Button>
          </div>
          <table css={style.table} data-testid="bank-feeds-table">
            <thead>
              <tr>
                <th scope="col">{translate('Bank')}</th>
                <th scope="col">{translate('Card Network')}</th>
                <th scope="col">{translate('Number of Cards Enrolled')}</th>
                <th scope="col">{translate('Bank Feed Status')}</th>
                <th scope="col">
                  <span css={style.visuallyHidden}>{translate('Actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr data-testid="bank-feeds-row">
                <td>{'Bank of America'}</td>
                <td>{'Mastercard'}</td>
                <td>{'1'}</td>
                <td>
                  <Pill>{translate('Not connected')}</Pill>
                </td>
                <td css={style.actionCell}>
                  <Button
                    variant="text"
                    onClick={handleConnectClick}
                    data-testid="bank-feeds-connect"
                  >
                    {`${translate('Connect Bank Feed')} →`}
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
          <div css={style.footer}>
            <span>{`${translate('Rows per page')}: 1`}</span>
          </div>
        </Box>
      </LayoutPage>
    </>
  )
}

export default CardFeeds
